import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { OperatorMark } from "@/components/mobile/OperatorLogo";
import { toast } from "sonner";
import { Undo2, PackageCheck } from "lucide-react";

const simTotal = (row) => (row.return_items || []).reduce((sum, item) => sum + item.qty, 0);
const itemsText = (items) => items.map((item) => `${item.qty} SIM ${item.operator_name}`).join(", ");

export const ReturnBadge = ({ row, testId }) => {
  if (!row.is_return) return null;
  const received = Boolean(row.return_received_at);
  const sims = simTotal(row);
  return <span title={received ? (sims ? `Rientrate: ${itemsText(row.return_items)}` : "Ricevuto, senza SIM da rientrare") : `Contenuto: ${row.content || "non indicato"}`}
    className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${received ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-teal-500/40 bg-teal-500/10 text-teal-600"}`} data-testid={testId}>
    {received ? <PackageCheck className="w-3 h-3" /> : <Undo2 className="w-3 h-3" />}
    {received ? `Reso ricevuto${sims ? ` · ${sims} SIM` : ""}` : "Reso"}
  </span>;
};

export const ReturnReceivedDialog = ({ row, onClose, onDone }) => {
  const [ops, setOps] = useState([]);
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!row) return;
    setQty({}); setNote("");
    api.get("/mobile-operators").then(({ data }) => setOps(data.items.filter((op) => op.kind === "sim"))).catch(() => toast.error("Operatori non disponibili: riprova."));
  }, [row]);
  const save = async () => {
    setBusy(true);
    try {
      const items = ops.map((op) => ({ operator: op.key, qty: Number(qty[op.key] || 0) })).filter((item) => item.qty > 0);
      const { data } = await api.post(`/admin/sim-stock/returns/${row.id}`, { items, note: note.trim() });
      toast.success(data.items.length ? `Reso ricevuto: ${itemsText(data.items)} rimesse nel magazzino Markoo` : "Reso segnato come ricevuto");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reso non registrato: riprova."); }
    finally { setBusy(false); }
  };
  return <Dialog open={!!row} onOpenChange={(open) => { if (!open) onClose(); }}>
    <DialogContent data-testid="return-received-dialog">
      <DialogHeader><DialogTitle>Reso ricevuto</DialogTitle><DialogDescription>Indica quante SIM sono arrivate: le tolgo dalla giacenza del dealer e le rimetto nel magazzino Markoo.</DialogDescription></DialogHeader>
      {row && <div className="space-y-3">
        <p className="rounded-lg border border-border p-3 text-sm [overflow-wrap:anywhere]" data-testid="return-declared-content"><span className="block text-xs text-muted-foreground mb-0.5">Contenuto dichiarato · {row.from_addr?.name || "dealer"}</span>{row.content || "Non indicato"}</p>
        {ops.map((op) => <div key={op.key} className="flex items-center gap-2 sm:gap-3">
          <OperatorMark op={op} />
          <Label htmlFor={`return-qty-${op.key}`} className="flex-1 min-w-0 leading-tight">{op.name}</Label>
          <Input id={`return-qty-${op.key}`} type="text" inputMode="numeric" className="w-16 shrink-0 text-right" placeholder="0" value={qty[op.key] ?? ""} onChange={(e) => setQty({ ...qty, [op.key]: e.target.value.replace(/\D/g, "").slice(0, 6) })} data-testid={`return-qty-${op.key}`} />
        </div>)}
        <div><Label htmlFor="return-note">Nota (facoltativa)</Label><Input id="return-note" className="mt-1" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} data-testid="return-note" /></div>
        <p className="text-xs text-muted-foreground">Lascia 0 se nel pacco c'erano solo materiali.</p>
        <Button className="w-full gap-2" disabled={busy || !ops.length} onClick={save} data-testid="return-received-confirm"><PackageCheck className="w-4 h-4" />{busy ? "Registro…" : "Conferma reso ricevuto"}</Button>
      </div>}
    </DialogContent>
  </Dialog>;
};
